import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Card, Button, Switch, Radio, Input, message, Space, Divider, Avatar, Row, Col, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Map, Marker, Popup } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import classNames from 'classnames/bind';
import style from './ShopMap.module.scss';
import ConnectionLayer from './ConnectionLayer';
import { HddFilled, SearchOutlined, ClearOutlined, StarFilled } from '@ant-design/icons';
import DatePicker from 'components/Datepicker';
import {
    groupShopsByGL,
    filterValidCoordinates,
    calculateMapBounds,
    getShopStyle,
    getCabinetStyle,
    filterVisibleByGroups,
    filterVisibleCabinets
} from '../../utils';
import { MAP_STYLE } from '../../constants';
const cx = classNames.bind(style);

// Memoized Shop Marker Component for performance
const ShopMarker = React.memo(({ shop, onShopClick, getShopStyleForComponent }) => {
    const handleClick = useCallback((e) => {
        e.originalEvent.stopPropagation();
        onShopClick(shop);
    }, [shop, onShopClick]);

    return (
        <Marker
            longitude={parseFloat(shop.lng)}
            latitude={parseFloat(shop.lat)}
            onClick={handleClick}
        >
            <div className={cx('shopMarkerContainer')}>
                {shop.openedAt ? (
                    <Avatar className={cx('shopMarkerAvatar')}>
                        <StarFilled style={{ color: '#FADB14', fontSize: '24px' }} />
                    </Avatar>
                ) : (
                    <Avatar style={{
                        backgroundColor: '#fde3cf',
                        ...getShopStyleForComponent(shop),
                        color: 'black',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                    }}>
                        {shop.name.charAt(0).toUpperCase()}
                    </Avatar>
                )}
            </div>
        </Marker>
    );
});

// Memoized Cabinet Marker Component for performance
const CabinetMarker = React.memo(({ cabinet, onCabinetClick, getCabinetStyleForComponent }) => {
    const handleClick = useCallback((e) => {
        e.originalEvent.stopPropagation();
        onCabinetClick(cabinet);
    }, [cabinet, onCabinetClick]);

    return (
        <Marker
            longitude={parseFloat(cabinet.lng)}
            latitude={parseFloat(cabinet.lat)}
            onClick={handleClick}
        >
            <div className={cx('cabinetMarkerContainer')}>
                <Button
                    shape="circle"
                    icon={<HddFilled style={{ fontSize: '18px' }} />}
                    style={{
                        ...getCabinetStyleForComponent(cabinet),
                        border: 'none',
                        boxShadow: '0 1px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    size="middle"
                />
            </div>
        </Marker>
    );
});

// Add display names for better debugging
ShopMarker.displayName = 'ShopMarker';
CabinetMarker.displayName = 'CabinetMarker';

export default function ShopMap({ listShop = [], listCabinet = [], loading = false }) {
    const { t } = useTranslation();
    const mapRef = useRef(null);
    const viewportUpdateTimeout = useRef(null);
    const [selectedShop, setSelectedShop] = useState(null);
    const [selectedCabinet, setSelectedCabinet] = useState(null);
    const [hiddenGroups, setHiddenGroups] = useState(new Set());
    const [showConnections, setShowConnections] = useState(true); // State cho Switch

    // Viewport optimization states
    const [viewportBounds, setViewportBounds] = useState(null);
    const [isInteracting, setIsInteracting] = useState(false);

    // States for filtering shops and cabinets
    const [showOpenedShops, setShowOpenedShops] = useState(true);
    const [showPlannedShops, setShowPlannedShops] = useState(true);
    const [showCabinets, setShowCabinets] = useState(true);

    // Search states
    const [searchType, setSearchType] = useState('shop');
    const [searchCode, setSearchCode] = useState('');

    // Date filter states
    const [selectedMonth, setSelectedMonth] = useState(null);

    // Group shops by GL and assign colors
    const shopGroups = useMemo(() => groupShopsByGL(listShop), [listShop]);

    // Filter shops that have valid coordinates
    const validShops = useMemo(() => filterValidCoordinates(listShop), [listShop]);

    // Filter cabinets that have valid coordinates
    const validCabinets = useMemo(() => filterValidCoordinates(listCabinet), [listCabinet]);

    // Calculate map bounds to fit all shops and cabinets
    const mapBounds = useMemo(() => calculateMapBounds(validShops, validCabinets), [validShops, validCabinets]);

    // Viewport optimization: check if a marker is within viewport bounds + buffer
    const isMarkerInViewport = useCallback((lng, lat) => {
        if (!viewportBounds) return true; // Show all if no viewport bounds yet

        const buffer = 0.01; // Buffer zone to render slightly outside viewport
        return (
            lng >= viewportBounds.west - buffer &&
            lng <= viewportBounds.east + buffer &&
            lat >= viewportBounds.south - buffer &&
            lat <= viewportBounds.north + buffer
        );
    }, [viewportBounds]);

    // Debounced viewport update to improve performance during pan/zoom
    const updateViewportBounds = useCallback((bounds) => {
        if (viewportUpdateTimeout.current) {
            clearTimeout(viewportUpdateTimeout.current);
        }

        viewportUpdateTimeout.current = setTimeout(() => {
            setViewportBounds(bounds);
            setIsInteracting(false);
        }, 100); // 100ms debounce
    }, []);

    // Map event handlers for performance optimization
    const handleMapMove = useCallback((evt) => {
        setIsInteracting(true);
        const bounds = evt.target.getBounds();
        updateViewportBounds({
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest()
        });
    }, [updateViewportBounds]);

    // Initialize viewport bounds when map loads
    useEffect(() => {
        if (mapRef.current) {
            const map = mapRef.current.getMap();
            if (map.isStyleLoaded()) {
                const bounds = map.getBounds();
                setViewportBounds({
                    north: bounds.getNorth(),
                    south: bounds.getSouth(),
                    east: bounds.getEast(),
                    west: bounds.getWest()
                });
            }
        }
    }, [mapBounds]);

    // Get shop color and style based on GL group and status
    const getShopStyleForComponent = useCallback((shop) => getShopStyle(shop, shopGroups), [shopGroups]);

    // Get cabinet style for buttons
    const getCabinetStyleForComponent = useCallback((cabinet) => getCabinetStyle(cabinet, validShops, shopGroups), [validShops, shopGroups]);

    // Memoized click handlers for better performance
    const handleShopClick = useCallback((shop) => {
        setSelectedShop(shop);
        setSelectedCabinet(null);
    }, []);

    const handleCabinetClick = useCallback((cabinet) => {
        setSelectedCabinet(cabinet);
        setSelectedShop(null);
    }, []);

    // Toggle GL group visibility với exclusive mode
    const [exclusiveGroup, setExclusiveGroup] = useState(null);

    const toggleGroupVisibility = (gl) => {
        if (exclusiveGroup === gl) {
            // Đang trong exclusive mode và click vào group đang hiện -> hiện tất cả
            setExclusiveGroup(null);
            setHiddenGroups(new Set());
        } else {
            // Chuyển sang exclusive mode cho group này
            setExclusiveGroup(gl);
            const newHiddenGroups = new Set();
            Object.keys(shopGroups).forEach(groupGl => {
                if (groupGl !== gl) {
                    newHiddenGroups.add(groupGl);
                }
            });
            setHiddenGroups(newHiddenGroups);
        }
    };

    // Handler functions for filter buttons
    const handleToggleOpenedShops = () => {
        setShowOpenedShops(!showOpenedShops);
    };

    const handleTogglePlannedShops = () => {
        setShowPlannedShops(!showPlannedShops);
    };

    const handleToggleCabinets = () => {
        setShowCabinets(!showCabinets);
    };

    // Search functionality
    const handleSearch = () => {
        if (!searchCode.trim()) {
            message.warning(t(searchType === 'shop' ? 'shopNotFound' : 'cabinetNotFound'));
            return;
        }

        let foundItem = null;

        if (searchType === 'shop') {
            // First try exact match, then partial match
            foundItem = listShop.find(shop =>
                shop.code && shop.code.toLowerCase() === searchCode.toLowerCase()
            );

            if (!foundItem) {
                foundItem = listShop.find(shop =>
                    shop.code && shop.code.toLowerCase().includes(searchCode.toLowerCase())
                );
            }
        } else {
            // First try exact match, then partial match
            foundItem = listCabinet.find(cabinet =>
                cabinet.code && cabinet.code.toLowerCase() === searchCode.toLowerCase()
            );

            if (!foundItem) {
                foundItem = listCabinet.find(cabinet =>
                    cabinet.code && cabinet.code.toLowerCase().includes(searchCode.toLowerCase())
                );
            }
        }

        if (foundItem) {
            if (foundItem.lng && foundItem.lat) {
                // Fly to the found location
                if (mapRef.current) {
                    mapRef.current.flyTo({
                        center: [parseFloat(foundItem.lng), parseFloat(foundItem.lat)],
                        duration: 2000
                    });
                }

                // Set the selected item
                if (searchType === 'shop') {
                    setSelectedShop(foundItem);
                    setSelectedCabinet(null);
                } else {
                    setSelectedCabinet(foundItem);
                    setSelectedShop(null);
                }

                message.success(`Found and navigated to ${foundItem.name || foundItem.code}`);
            } else {
                message.warning('Item found but no coordinates available');
            }
        } else {
            message.error(t(searchType === 'shop' ? 'shopNotFound' : 'cabinetNotFound'));
        }
    };

    const handleSearchTypeChange = (e) => {
        setSearchType(e.target.value);
        setSearchCode(''); // Clear search code when changing type
    };

    const handleClearSearch = () => {
        setSearchCode('');
        setSelectedShop(null);
        setSelectedCabinet(null);
    };

    // Month filter handlers
    const handleMonthChange = (date) => {
        setSelectedMonth(date);
    };

    // Filter visible shops and cabinets based on hidden groups and filter states
    const visibleShops = useMemo(() => {
        let filtered = filterVisibleByGroups(validShops, hiddenGroups);

        // Apply shop status filters
        filtered = filtered.filter(shop => {
            if (!shop.openedAt && !showOpenedShops) return false;
            if (shop.openedAt && !showPlannedShops) return false;
            return true;
        });

        // Apply month filter (only when showPlannedShops is true)
        if (selectedMonth && showPlannedShops) {
            filtered = filtered.filter(shop => {
                if (!shop.openedAt) return false; // Skip shops without openedAt date
                // Compare month and year
                return shop.openedAt === selectedMonth.format('MM/YYYY') || shop.openedAt === null;
            });
        }

        return filtered;
    }, [validShops, hiddenGroups, showOpenedShops, showPlannedShops, selectedMonth]);

    // Viewport-optimized shops - only render those in viewport during interaction
    const viewportOptimizedShops = useMemo(() => {
        if (!isInteracting || !viewportBounds) return visibleShops;

        return visibleShops.filter(shop =>
            isMarkerInViewport(parseFloat(shop.lng), parseFloat(shop.lat))
        );
    }, [visibleShops, isInteracting, viewportBounds, isMarkerInViewport]);

    const visibleCabinets = useMemo(() => {
        if (!showCabinets) return [];
        return filterVisibleCabinets(validCabinets, validShops, hiddenGroups);
    }, [validCabinets, validShops, hiddenGroups, showCabinets]);

    // Viewport-optimized cabinets - only render those in viewport during interaction
    const viewportOptimizedCabinets = useMemo(() => {
        if (!isInteracting || !viewportBounds) return visibleCabinets;

        return visibleCabinets.filter(cabinet =>
            isMarkerInViewport(parseFloat(cabinet.lng), parseFloat(cabinet.lat))
        );
    }, [visibleCabinets, isInteracting, viewportBounds, isMarkerInViewport]);

    return (
        <Space direction='vertical' style={{ width: '100%' }}>
            {/* GL toggle */}
            <Card>
                <Row gutter={[0, 8]}>
                    <Col>
                        <Tag color='gold' className={cx('glGroupItem')} onClick={() => {
                            setExclusiveGroup(null);
                            setHiddenGroups(new Set());
                        }}>
                            <div
                                className={cx('glGroupColorCircle')}
                                style={{ backgroundColor: '#fff' }} // Default color for all groups
                            />
                            <span className={cx('glGroupText')}>
                                {t('all')}
                            </span>
                        </Tag>
                    </Col>
                    {Object.entries(shopGroups).map(([gl, group]) => {
                        const isHidden = hiddenGroups.has(gl);
                        const isExclusive = exclusiveGroup === gl;

                        return (
                            <Col
                                key={gl}
                                onClick={() => toggleGroupVisibility(gl)}
                            >
                                <Tag className={cx('glGroupItem', {
                                    exclusive: isExclusive,
                                    hidden: isHidden
                                })}>
                                    <div
                                        className={cx('glGroupColorCircle', { hidden: isHidden })}
                                        style={{ backgroundColor: group.color }}
                                    />
                                    <span className={cx('glGroupText')}>
                                        {gl} ({group.shops.length})
                                    </span>
                                </Tag>
                            </Col>
                        );
                    })}
                </Row>
            </Card>
            <div className={cx('shopMap')}>
                {/* Search Section - Moved outside and above the map */}
                <Card
                    size="small"
                    className={cx('searchCard')}
                >
                    <div className={cx('searchSection')}>
                        <div className={cx('searchTypeSelector')}>
                            <Radio.Group value={searchType} onChange={handleSearchTypeChange} size="small">
                                <Radio.Button value="shop">{t('shop')}</Radio.Button>
                                <Radio.Button value="cabinet">{t('cabinetSingle')}</Radio.Button>
                            </Radio.Group>
                        </div>
                        <div className={cx('searchControls')}>
                            <Input
                                value={searchCode}
                                onChange={(e) => setSearchCode(e.target.value)}
                                onPressEnter={handleSearch}
                                size="small"
                                allowClear
                            />
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleSearch}
                                    size="small"
                                    disabled={!searchCode.trim()}
                                >
                                    {t('search') || 'Search'}
                                </Button>
                                <Button
                                    icon={<ClearOutlined />}
                                    onClick={handleClearSearch}
                                    size="small"
                                    title={t('clearSearch') || 'Clear search'}
                                />
                            </Space>
                        </div>
                    </div>

                    {/* Legend */}
                    <Divider dashed style={{ margin: '12px 0 6px' }} />

                    <div className={cx('infoStats')}>
                        <span className={cx('infoStatsLabel')}>{t('shopsOnMap')}:</span> {visibleShops.length}
                    </div>
                    <div className={cx('infoStats')}>
                        <span className={cx('infoStatsLabel')}>{t('cabinetsOnMap')}:</span> {validCabinets.length}
                    </div>
                    {validShops.length < listShop.length && (
                        <div className={cx('warningText')}>
                            {t('someShopsWithoutCoordinates') || `${listShop.length - validShops.length} shops without coordinates`}
                        </div>
                    )}
                    {validCabinets.length < listCabinet.length && (
                        <div className={cx('warningText')}>
                            {t('someCabinetsWithoutCoordinates') || `${listCabinet.length - validCabinets.length} cabinets without coordinates`}
                        </div>
                    )}

                    <div className={cx('controlsSection')}>
                        <div className={cx('switchContainer')}>
                            <Switch
                                checked={showConnections}
                                onChange={setShowConnections}
                                size="small"
                            />
                            <span className={cx('switchLabel')}>
                                {t('showConnections') || 'Show connections'}
                            </span>
                        </div>
                        <div className={cx('notesHeader')}>{t('model')}:</div>
                        <div className={cx('noteItem')}>
                            <Button
                                type={showOpenedShops ? 'primary' : 'default'}
                                shape="circle"
                                style={{ marginRight: '4px' }}
                                onClick={handleToggleOpenedShops}
                            >R</Button>
                            <span>{t('opened')}</span>
                        </div>
                        <div className={cx('noteItem')}>
                            <Button
                                type={showPlannedShops ? 'primary' : 'default'}
                                shape="circle"
                                icon={<StarFilled style={{ color: '#FADB14', fontSize: '24px' }} />}
                                style={{ marginRight: '4px' }}
                                onClick={handleTogglePlannedShops}
                            />
                            <span>{t('planned')}</span>
                        </div>
                        <div className={cx('noteItem')}>
                            <Button
                                type={showCabinets ? 'primary' : 'default'}
                                shape="circle"
                                icon={<HddFilled className={cx('noteIconSmall')} />}
                                style={{ marginRight: '4px' }}
                                onClick={handleToggleCabinets}
                            />
                            <span>{t('cabinetSingle')}</span>
                        </div>
                        {/* Month Filter Section - Only show when showPlannedShops is true */}
                        {showPlannedShops && (
                            <div className={cx('monthFilterSection')} style={{ marginTop: 12, marginBottom: 12 }}>
                                <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: 6 }}>
                                    {t('timeOpened')}:
                                </div>
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <DatePicker
                                        value={selectedMonth}
                                        onChange={handleMonthChange}
                                        placeholder=''
                                        size="small"
                                        style={{ width: '100%' }}
                                        format="MM/YYYY"
                                        picker="month"
                                    />
                                </Space>
                            </div>
                        )}
                    </div>
                </Card>

                <Map
                    ref={mapRef}
                    mapLib={maplibregl}
                    initialViewState={mapBounds}
                    style={{ width: '100%', height: 'calc(100vh - 180px)' }}
                    mapStyle={MAP_STYLE}
                    maxZoom={20}
                    minZoom={5}
                    attributionControl={true}
                    interactive={true}
                    dragPan={true}
                    dragRotate={false}
                    scrollZoom={true}
                    touchZoom={true}
                    touchRotate={false}
                    keyboard={true}
                    doubleClickZoom={true}
                    boxZoom={true}
                    onMove={handleMapMove}
                    onMoveEnd={handleMapMove}
                >
                    {/* Connection Layer for lines and polygons - Render first so it stays behind markers */}
                    {showConnections && (
                        <ConnectionLayer
                            shopGroups={shopGroups}
                            validShops={validShops}
                            validCabinets={validCabinets}
                            hiddenGroups={hiddenGroups}
                        />
                    )}

                    {/* Shop markers - Use optimized viewport rendering */}
                    {(isInteracting ? viewportOptimizedShops : visibleShops).map((shop, index) => (
                        <ShopMarker
                            key={shop._id || index}
                            shop={shop}
                            onShopClick={handleShopClick}
                            getShopStyleForComponent={getShopStyleForComponent}
                        />
                    ))}

                    {/* Cabinet markers - Use optimized viewport rendering */}
                    {(isInteracting ? viewportOptimizedCabinets : visibleCabinets).map((cabinet, index) => (
                        <CabinetMarker
                            key={`cabinet-${cabinet._id || index}`}
                            cabinet={cabinet}
                            onCabinetClick={handleCabinetClick}
                            getCabinetStyleForComponent={getCabinetStyleForComponent}
                        />
                    ))}

                    {/* Shop popup */}
                    {selectedShop && (
                        <Popup
                            longitude={parseFloat(selectedShop.lng)}
                            latitude={parseFloat(selectedShop.lat)}
                            onClose={() => setSelectedShop(null)}
                            closeButton={true}
                            closeOnClick={false}
                            offset={25}
                        >
                            <div className={cx('popupContent')}>
                                <h4 className={cx('popupTitle')}>{selectedShop.name}</h4>
                                <p className={cx('popupText')}>
                                    <span className={cx('popupLabel')}>{t('shopCode')}:</span> {selectedShop.code}
                                </p>
                                <p className={cx('popupText')}>
                                    <span className={cx('popupLabel')}>{t('address')}:</span> {selectedShop.address}
                                </p>
                                <p className={cx('popupText')}>
                                    <span className={cx('popupLabel')}>{t('departmentManager')}:</span> {selectedShop.gl}
                                </p>
                                <p className={cx('popupText')}>
                                    <span className={cx('popupLabel')}>{t('status')}:</span>
                                    <span style={{
                                        color: !selectedShop.openedAt ? '#52c41a' : '#FADB14',
                                        fontWeight: 'bold',
                                        marginLeft: '5px'
                                    }}>
                                        {!selectedShop.openedAt ? t('opened') : t('planned')}
                                    </span>
                                </p>
                            </div>
                        </Popup>
                    )}

                    {/* Cabinet popup */}
                    {selectedCabinet && (
                        <Popup
                            longitude={parseFloat(selectedCabinet.lng)}
                            latitude={parseFloat(selectedCabinet.lat)}
                            onClose={() => setSelectedCabinet(null)}
                            closeButton={true}
                            closeOnClick={false}
                            offset={25}
                        >
                            <div className={cx('popupContent')}>
                                <h4 className={cx('popupTitle')}>{t('cabinetSingle')}</h4>
                                <p className={cx('popupText')}>
                                    <span className={cx('popupLabel')}>{t('cabinetCode')}:</span> {selectedCabinet.code}
                                </p>
                                <p className={cx('popupText')}>
                                    <span className={cx('popupLabel')}>{t('shopCode')}:</span> {selectedCabinet.shop_code}
                                </p>
                                <p className={cx('popupText')}>
                                    <span className={cx('popupLabel')}>{t('address')}:</span> {selectedCabinet.address}
                                </p>
                            </div>
                        </Popup>
                    )}

                </Map>
            </div>
        </Space>
    );
}
